import { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { P, SvgIcon, Toggle } from '@salecycle/ui-component-library';
import { useTrigger } from '../../hooks/useTrigger';
import { TooltipWithTitle } from '@salecycle/experimental/TooltipWithTitle';
import { useTranslation } from 'react-i18next';
import { SegmentationModal, SegmentationRule } from '@salecycle/experimental/Modal/SegmentationModal';
import { SelectSegmentationButton } from '../../../components/SelectSegmentationButton';
import { ControlCard } from 'sections/onsite-campaigns/pages/CampaignEditor/components/ControlCard';
import { OnsiteSegmentationOptions } from 'models/segmentation/OnsiteSegmentationOptionsFactory';
import { SelectedSegmentationRules } from '@salecycle/experimental/Segmentation/SelectedSegmentationRules';

const SelectedRules = () => {
  const { segmentation } = useTrigger();
  const { t } = useTranslation('campaigns');

  return <SelectedSegmentationRules rules={segmentation} t={t} useMargins />;
};

const RemoveRedundantMarginFromToggle = styled.div`
  margin-top: -8px;
`;

export const SegmentationConfiguration = () => {
  const { t } = useTranslation('campaigns');
  const { segmentation, clearSegmentation, segmentationChosen, saveTriggerSegmentation } = useTrigger();

  const theme = useTheme();

  const [allVisitors, setAllVisitors] = useState(!segmentation);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => setAllVisitors(!segmentation), [segmentation]);

  const toggleAllVisitors = useCallback(() => {
    const toggledValue = !allVisitors;
    if (toggledValue) {
      clearSegmentation();
    } else {
      segmentationChosen();
    }

    setAllVisitors(toggledValue);
  }, [allVisitors, clearSegmentation, segmentationChosen]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openModal = useCallback(() => setIsModalOpen(true), []);

  const onSegmentationSaved = useCallback(
    (segmentation: SegmentationRule<any, any, any>[][]) => {
      if (segmentation.length) {
        saveTriggerSegmentation(segmentation);
      } else {
        // This sets the segmentation as an empty array. Ideally we'd have a more appropriately named action.
        segmentationChosen();
      }
    },
    [saveTriggerSegmentation, segmentationChosen],
  );

  const factory = useMemo(() => new OnsiteSegmentationOptions(t), [t]);

  return (
    <>
      <ControlCard aria-label={t('onsite.editor.workflow.triggerMessage.drawer.segmentation.triggerConfigAriaLabel')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: '16px' }}>
          <P>{t('onsite.editor.workflow.triggerMessage.drawer.segmentation.bodyText')}</P>
          <TooltipWithTitle
            title={t('onsite.editor.workflow.triggerMessage.drawer.segmentation.bodyTextTooltipTitle')}
            content={t('onsite.editor.workflow.triggerMessage.drawer.segmentation.bodyTextTooltipContent')}
          >
            <div style={{ marginTop: '-7px' }}>
              <SvgIcon name="circle-question" size="16" color={theme.designToken.primary700} />
            </div>
          </TooltipWithTitle>
        </div>
        <Toggle ariaLabel="All visitors" label="All visitors" onChange={toggleAllVisitors} checked={allVisitors} />
        <RemoveRedundantMarginFromToggle>
          <SelectedRules />
          <SelectSegmentationButton segmentation={segmentation} isAllVisitors={allVisitors} openModal={openModal} />
        </RemoveRedundantMarginFromToggle>
      </ControlCard>

      <SegmentationModal
        open={isModalOpen}
        onClose={closeModal}
        onSave={onSegmentationSaved}
        initialRules={segmentation}
        optionsFactory={factory}
        hasOROption
      />
    </>
  );
};
